
import { useState, useEffect } from "react";
import NodeIcon from "./NodeIcon";
import NodeHeaderEditMode from "./NodeHeaderEditMode";
import NodeHeaderSelectMode from "./NodeHeaderSelectMode";
import { NodeHeaderProps } from "./NodeHeaderTypes";

const NodeHeader = ({ 
  category = "Message",
  color,
  onCategoryChange,
  isClientMode = false
}: NodeHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(category);
  const [displayCategory, setDisplayCategory] = useState(category);

  // Update display state when category prop changes
  useEffect(() => {
    console.log("NodeHeader category prop changed:", category);
    setDisplayCategory(category);
    setEditValue(category);
  }, [category]);

  console.log("NodeHeader rendering:", { 
    category, 
    displayCategory, 
    isEditing, 
    editValue,
    propsCategory: category 
  });

  const handleCategorySelect = (newCategory: string) => {
    console.log("Category selected in dropdown:", newCategory);
    console.log("Current onCategoryChange function:", typeof onCategoryChange);
    
    setDisplayCategory(newCategory); // Update display immediately
    setEditValue(newCategory);
    
    // Call the parent's onChange handler
    console.log("Calling onCategoryChange with:", newCategory);
    try {
      onCategoryChange(newCategory);
      console.log("onCategoryChange called successfully");
    } catch (error) {
      console.error("Error calling onCategoryChange:", error);
    }
  };

  const handleEditComplete = () => {
    console.log("Edit complete:", { editValue, displayCategory, hasChanged: editValue.trim() !== displayCategory });
    setIsEditing(false);
    if (editValue.trim() !== displayCategory && editValue.trim() !== "") {
      const newCategory = editValue.trim();
      setDisplayCategory(newCategory);
      onCategoryChange(newCategory);
    } else {
      setEditValue(displayCategory); // Reset if empty or unchanged
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleEditComplete();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsEditing(false);
      setEditValue(displayCategory);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    console.log("Double click on header - starting edit mode");
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(displayCategory);
  };

  const handleSingleClick = (e: React.MouseEvent) => {
    // Allow single click to edit if user clicks on the text area specifically
    const target = e.target as HTMLElement;
    if (target.closest('.category-text-area')) {
      console.log("Single click on category text - starting edit mode");
      e.preventDefault();
      e.stopPropagation();
      setIsEditing(true);
      setEditValue(displayCategory);
    }
  };

  const handleSelectOpenChange = (open: boolean) => {
    console.log("Select dropdown open state changed:", open);
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    console.log("Select trigger clicked");
    e.preventDefault();
    e.stopPropagation();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    console.log("Select content clicked");
    e.stopPropagation();
  };

  const handleItemClick = (e: React.MouseEvent) => {
    console.log("Select item clicked");
    e.stopPropagation();
  };

  return (
    <div 
      className="w-full px-3 py-1 text-xs font-medium flex items-center justify-center"
      style={{
        background: 'transparent',
        color: 'hsl(var(--muted-foreground))',
        borderBottom: 'none',
        position: 'relative',
        zIndex: 10
      }}
      onDoubleClick={handleDoubleClick}
      onClick={handleSingleClick}
    >
      <div className="flex items-center w-full justify-center" style={{ pointerEvents: 'all' }}>
        <NodeIcon category={displayCategory} />
        
        {isEditing ? (
          <div className="nodrag">
            <NodeHeaderEditMode
              editValue={editValue}
              setEditValue={setEditValue}
              onEditComplete={handleEditComplete}
              onKeyDown={handleKeyDown}
            />
          </div>
        ) : (
          <div className="nodrag">
            <NodeHeaderSelectMode
              displayCategory={displayCategory}
              onCategorySelect={handleCategorySelect}
              onSelectOpenChange={handleSelectOpenChange}
              onTriggerClick={handleTriggerClick}
              onContentClick={handleContentClick}
              onItemClick={handleItemClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeHeader;
